import { useEffect, useRef } from 'react'

import { useLocation } from 'react-router-dom'

/**
 * Forces a full page load on the next route (pathname) change once `enabled`
 * is true, so stale sessions pick up the newly deployed bundle at a natural
 * navigation boundary instead of holding onto old code indefinitely.
 *
 * The reload runs after the SPA navigation has already updated the URL, so
 * the browser re-enters at the destination route. Query- and hash-only
 * changes (dialogs, filters) don't trigger it, and it fires at most once —
 * the page is unloading anyway.
 */
export function useReloadOnNavigation(
  enabled: boolean,
  onReload: () => void = (): void => window.location.reload(),
): void {
  const location = useLocation()
  const previousPathnameRef = useRef(location.pathname)
  const reloadedRef = useRef(false)

  // Latest values in refs so the effect below re-runs only on pathname change.
  const enabledRef = useRef(enabled)
  enabledRef.current = enabled
  const onReloadRef = useRef(onReload)
  onReloadRef.current = onReload

  useEffect(() => {
    if (location.pathname === previousPathnameRef.current) return
    previousPathnameRef.current = location.pathname

    if (!enabledRef.current || reloadedRef.current) return
    reloadedRef.current = true
    onReloadRef.current()
  }, [location.pathname])
}
